import React from 'react';
import TimeBankBar from './TimeBankBar';
import { useTableGeometryCtx } from './useTableGeometry';
import { usableRangeLabel, holeCountTip, usableRangeTip } from './holeIndicators';
import './gameroom.css';

const Player = ({ seatNumber, player, onSitClick, gameId, isPending, pendingUsername, isSeated, activePlayerPosition, isActiveTurn, turnStartTime, mySeat, isAway, winnerLabel, isPupTargetable, isPupPlayer, onPupTarget }) => {
  // Anchor points on the table's rail ring, computed from the live table size.
  // Empty/Sit buttons and the avatar sit on the ring; the nameplate hangs below it.
  const { seatStyle, seatNameplateStyle } = useTableGeometryCtx();
  const positionStyle = seatStyle(seatNumber);
  const nameplateStyle = seatNameplateStyle(seatNumber);

  // isPending = local state (this browser clicked Sit)
  // player?.isPending = remote state (another browser clicked Sit, received via WebSocket)
  if (isPending || player?.isPending) {
    const displayName = isPending ? pendingUsername : player.username;
    return (
      <div className="seat player-info" style={positionStyle}>
        <div className="player-name">{displayName}</div>
        <div className="player-chips">selecting...</div>
      </div>
    );
  }

  if (!player || player.isEmpty) {
    if (isSeated) {
      return (
        <button className="seat" style={positionStyle} disabled>
          Empty
        </button>
      );
    }
    return (
      <button
        className="seat"
        style={positionStyle}
        onClick={() => onSitClick(gameId, seatNumber)}
      >
        Sit
      </button>
    );
  }

  // Fade the whole bubble when the seat isn't actively participating in the hand: folded,
  // sitting out, or away (the avatar and cards get the same .player-dimmed in GameTable).
  const inactive = player.isFolded || player.isSittingOut || isAway;

  // ── The two circles flanking the nameplate (see .hole-indicator in gameroom.css) ──────────
  // Public tactical info, like the tier pips: how many hole cards this seat holds, and how many
  // of them its best five may use. Gated on actually holding cards — an undealt seat has none
  // to describe, and the ceiling keeps its value between rounds until the next reset, so the
  // count is the honest gate. The usable circle additionally waits for real numbers so a payload
  // that predates the fields can never print "undefined". No dimming class needed: .player-dimmed
  // fades the whole bubble, circles included.
  const held = player.numHoleCards || 0;
  const { minHoleUsed, maxHoleUsed } = player;
  const isHero = seatNumber === mySeat;
  const showUsable = held > 0
    && Number.isFinite(minHoleUsed) && Number.isFinite(maxHoleUsed) && maxHoleUsed > 0;

  return (
    <div
      className={`seat player-info ${inactive ? 'player-dimmed' : ''} ${seatNumber === activePlayerPosition ? 'player-active' : ''} ${isPupTargetable ? 'pup-targetable' : ''} ${isPupPlayer ? 'pup-player' : ''}`}
      style={nameplateStyle}
      data-seat={seatNumber}
      // Targeting window of the hero's own pending Power-Up: clicking a highlighted seat
      // picks it as the card's target (GameTable decides which seats qualify).
      onClick={isPupTargetable ? () => onPupTarget(seatNumber) : undefined}
    >
      <div className="player-name">{player.username}</div>
      <div className="player-chips">{player.chips}</div>
      {winnerLabel && <div className="player-winner-hand">{winnerLabel}</div>}
      {isAway ? (
        <div className="player-status player-status-away">Away</div>
      ) : (
        <>
          {player.isFolded && <div className="player-status">Folded</div>}
          {player.isAllIn && <div className="player-status">All In</div>}
          {/* Flying Blind Power-Up: this seat plays the round without seeing its own cards
              (public — the whole table watched the play/opt-in). */}
          {player.blind && !player.isFolded && <div className="player-status">Flying Blind</div>}
          {/* Half Blind Bandit: one of this seat's own cards is masked from them; callers/
              raisers against the bandit's aggression pay the blind-side-pot surcharge. */}
          {player.halfBlind && !player.isFolded && <div className="player-status">Half Blind</div>}
        </>
      )}
      {isActiveTurn && (
        <TimeBankBar
          turnStartTime={turnStartTime}
          reserveSeconds={player.reserveTimeBank ?? 0}
          isHero={isHero}
        />
      )}
      {/* Hole Indicator — LEFT of the box, its center at X = -(W + 1.25r). No `title` here: a
          native tooltip would fire a second, differently-timed bubble over our own. */}
      {held > 0 && (
        <div
          className="hole-indicator hole-indicator--count"
          role="img"
          aria-label={holeCountTip(held, isHero)}
        >
          <span className="hole-indicator-value">{held}</span>
          <span className="hole-indicator-tip hole-indicator-tip--left">
            {holeCountTip(held, isHero)}
          </span>
        </div>
      )}
      {/* Usable Hole Indicator — RIGHT of the box, its center at X = +(W + 1.25r). */}
      {showUsable && (
        <div
          className="hole-indicator hole-indicator--usable"
          role="img"
          aria-label={usableRangeTip(minHoleUsed, maxHoleUsed, held, isHero)}
        >
          <span className="hole-indicator-value">{usableRangeLabel(minHoleUsed, maxHoleUsed)}</span>
          <span className="hole-indicator-tip hole-indicator-tip--right">
            {usableRangeTip(minHoleUsed, maxHoleUsed, held, isHero)}
          </span>
        </div>
      )}
    </div>
  );
};

export default Player;
